// ---------------------------------------------------------------------------
// /pricing — three-tier pricing page (OPUS-D20)
// ---------------------------------------------------------------------------
// Implements features #17 (three-tier Starter/Agency/Enterprise) and #53
// (self-serve pricing page) on the dash surface. The page:
//   - Fetches active tiers from /api/v1/pricing-page/tiers via TanStack Query
//     v5 and merges them with a locally-declared canonical deck so the three
//     cards always render.
//   - Highlights the tenant's active tier with a "Current plan" badge.
//   - Emerald primary "Choose" CTA, sky secondary "See details" CTA.
//   - Gradient emerald → sky heading, light theme, Inter, 6–8px rounded
//     corners, zinc borders — per the CLAUDE.md brand rules. No emojis. No
//     competitor names.
//
// The dash scaffold (opD01) uses programmatic router assembly, so we expose a
// factory `createPricingRoute` that the scaffold owner can wire in with one
// line — same shape as opD04 so root can batch the wire-ups together.
// ---------------------------------------------------------------------------

import type { RootRoute } from "@tanstack/react-router";
import { createRoute } from "@tanstack/react-router";
import { PricingTable } from "@/features/pricing/PricingTable";
import {
	resolveCurrentPlan,
	usePricingTiers,
	type PricingTierView,
} from "@/features/pricing/api";

function PricingPage() {
	const currentPlan = resolveCurrentPlan();
	const query = usePricingTiers();
	const tiers: PricingTierView[] = query.data ?? [];

	const handleChoose = (tier: PricingTierView) => {
		// Deep-linking to a self-serve checkout / sales form lands in a later
		// agent (OPUS-D55 cc-subscribe). For now we pipe the selection into a
		// lightweight log breadcrumb — sufficient for the smoke test and
		// harmless in prod.
		if (typeof window !== "undefined") {
			// eslint-disable-next-line no-console -- scaffold-time breadcrumb
			console.log("[pricing] chose tier", tier.tier_code);
		}
	};

	const handleDetails = (tier: PricingTierView) => {
		if (typeof window !== "undefined") {
			// eslint-disable-next-line no-console -- scaffold-time breadcrumb
			console.log("[pricing] details", tier.tier_code);
		}
	};

	return (
		<main className="mx-auto w-full max-w-[1200px] px-6 py-12">
			<header className="mb-10 flex flex-col items-center gap-3 text-center">
				<h1 className="text-brand-gradient text-4xl font-semibold tracking-tight sm:text-5xl">
					Pricing that scales with your volume
				</h1>
				<p className="max-w-2xl text-base text-[color:var(--color-text-muted)]">
					Start on a self-serve plan, grow into a multi-brand agency console,
					or commit to an enterprise term with SSO, SLA, and regional data
					residency. Every plan includes unlimited clicks and the full
					attribution engine.
				</p>
			</header>

			<PricingTable
				tiers={tiers}
				currentPlan={currentPlan}
				isLoading={query.isLoading || query.isFetching}
				isError={query.isError}
				onRetry={() => query.refetch()}
				onChoose={handleChoose}
				onDetails={handleDetails}
			/>

			<section
				aria-labelledby="pricing-footnotes"
				className="mt-12 flex flex-col gap-3 rounded-[8px] border bg-[color:var(--color-bg-alt)] px-6 py-5 text-sm text-[color:var(--color-text-muted)]"
				style={{ borderColor: "var(--color-border)" }}
			>
				<h2
					id="pricing-footnotes"
					className="text-sm font-semibold text-[color:var(--color-text)]"
				>
					How our pricing works
				</h2>
				<p>
					Conversions are billed monthly against a simple included bucket plus
					a flat overage. Agency and Enterprise plans are custom-quoted to
					match committed volume, required support level, and compliance
					posture (SOC 2, GDPR/CCPA, data residency).
				</p>
				<p>
					Every plan ships with the same core attribution engine — state-by-
					state tracking, fraud signals, multi-touch models, and postback
					delivery — so you never pay for features you already need just to
					unlock the ones you want.
				</p>
			</section>
		</main>
	);
}

// ---- tanstack-router route export ---------------------------------------
// Factory wiring pattern (same as opD04): root imports `createPricingRoute`,
// calls it with the root route, and appends the returned node to the tree:
//
//   import { createPricingRoute } from "./routes/pricing";
//   const pricingRoute = createPricingRoute(rootRoute);
//   rootRoute.addChildren([..., pricingRoute]);

// biome-ignore lint/suspicious/noExplicitAny: RootRoute generics are noisy and
// the scaffold consumer only needs a simple "pass the parent" ergonomic.
export function createPricingRoute(
	parent: RootRoute<any, any, any, any, any, any, any, any, any>,
) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/pricing",
		component: PricingPage,
	});
}

export { PricingPage };
export default PricingPage;
